import { useEffect, useRef } from "react";

export interface TerminalLine {
  id: string;
  /** Visual prefix. Auto-coloured by status. */
  status: "running" | "ok" | "fail" | "info";
  text: string;
}

export interface MacTerminalProps {
  title?: string;
  lines: TerminalLine[];
  /** Banner shown below the lines when the run has finished. */
  banner?: { kind: "success" | "failure"; text: string };
  className?: string;
}

/**
 * macOS-style terminal chrome with auto-scrolling body. Read-only — the
 * server drives every line. Three traffic-light dots, rounded chrome,
 * monospace font, soft glow.
 */
export function MacTerminal({
  title = "dr-validate — ssh validator@dr",
  lines,
  banner,
  className = "",
}: MacTerminalProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines.length, banner?.kind]);

  return (
    <div
      className={`overflow-hidden rounded-xl border border-terminal-border shadow-2xl ${className}`}
    >
      {/* Chrome */}
      <div className="flex items-center gap-2 bg-terminal-chrome px-3 py-2">
        <span className="h-3 w-3 rounded-full bg-terminal-fail" aria-hidden />
        <span className="h-3 w-3 rounded-full bg-terminal-warn" aria-hidden />
        <span className="h-3 w-3 rounded-full bg-terminal-ok" aria-hidden />
        <div className="flex-1 text-center text-xs text-white/70 select-none">
          {title}
        </div>
        <span className="w-12" />
      </div>
      {/* Body */}
      <div
        ref={scrollRef}
        className="h-[420px] overflow-y-auto bg-terminal-bg p-4 font-mono text-sm leading-relaxed text-white/90"
      >
        {lines.map((line) => (
          <Line key={line.id} line={line} />
        ))}
        {banner ? (
          <div className="mt-6">
            {banner.kind === "success" ? (
              <pre className="text-terminal-ok whitespace-pre">
{`
  ┌──────────────────────────────────────────┐
  │   ✓  ${pad(banner.text, 36)} │
  └──────────────────────────────────────────┘
`}
              </pre>
            ) : (
              <pre className="text-terminal-fail whitespace-pre">
{`
  ┌──────────────────────────────────────────┐
  │   ✗  ${pad(banner.text, 36)} │
  └──────────────────────────────────────────┘
`}
              </pre>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Line({ line }: { line: TerminalLine }) {
  const prefix =
    line.status === "ok"
      ? "[ OK ]"
      : line.status === "fail"
        ? "[FAIL]"
        : line.status === "running"
          ? "[ .. ]"
          : "      ";
  const cls =
    line.status === "ok"
      ? "text-terminal-ok"
      : line.status === "fail"
        ? "text-terminal-fail"
        : line.status === "running"
          ? "text-terminal-warn"
          : "text-white/70";
  return (
    <div className="whitespace-pre-wrap break-words">
      <span className={cls}>{prefix}</span>{" "}
      <span>{line.text}</span>
    </div>
  );
}

function pad(s: string, n: number): string {
  if (s.length >= n) return s.slice(0, n);
  return s + " ".repeat(n - s.length);
}
